import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { toast } from 'react-toastify';
import { 
    FormControl, 
    TextField,
    Box,
    Autocomplete
} from "@mui/material";

const DefaultFields = ({formData, handleChallanFormData, handleAttachmentChange, schemeLoading, department, screenType, totalAmount}) => {
    const { authData, financialYears } = useSelector((state) => state.authData);
    const fy_list = financialYears?.payload?.fy_list || [];
    const departmentList = useMemo(() => {
        const list = Array.isArray(department) ? department : [];
        // Deduplicate by id to prevent options appearing to grow
        const seen = new Set();
        return list.filter((dept) => {
            const key = String(dept.id);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [department]);
    const selectedDepartment = useMemo(
        () => departmentList.find((dept) => String(dept.id) === String(formData.department_id)) || null,
        [departmentList, formData.department_id]
    );
    const selectedFinancialYear = fy_list.find((fy) => String(fy.id) === String(formData.financial_id));
    const min_reappropriate_order_date = selectedFinancialYear
        ? `${selectedFinancialYear.start_year}-04-01`
        : null;

    const isReappropriateOrderDateValid = (reappropriateOrderDate, minDate) => {
        if (!reappropriateOrderDate || !minDate) {
        return false;
        }
        return reappropriateOrderDate >= minDate;
    };

    const handleReappropriateOrderDateChange = (e) => {
        const value = e.target.value;

        if (!formData.financial_id) {
            toast.error('Please select Financial Year first.', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        if (min_reappropriate_order_date && value && !isReappropriateOrderDateValid(value, min_reappropriate_order_date)) {
            toast.error(
                `Reappropriation Order Date cannot be before ${min_reappropriate_order_date}.`,
                {
                    position: toast.POSITION.TOP_CENTER,
                }
            );
            return;
        }

        handleChallanFormData(e);
    };
    return (
        <>
            <div className='col-md-4 p-2'>
                <Autocomplete
                    options={departmentList}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedDepartment}
                    onChange={(event, newValue) => {
                        handleChallanFormData({
                            target: {
                                name: 'department_id',
                                value: newValue ? newValue.id : ''
                            }
                        });
                    }}
                    filterOptions={(options, { inputValue }) => {
                        const term = inputValue.trim().toLowerCase();
                        if (!term) return options;
                        return options.filter((opt) =>
                            opt.name.toLowerCase().includes(term)
                        );
                    }}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    disabled={screenType === 'View'}
                    noOptionsText="No departments found"
                    renderInput={(params) => (
                        <TextField {...params} label="Department *" />
                    )}
                />
            </div>
            <div className="col-md-4 p-2">
                <FormControl fullWidth>
                    <TextField 
                    id="reappropriate_order_no"
                    label="Reappropriation Order No." 
                    variant="outlined" 
                    type="text"
                    name="reappropriate_order_no"
                    onChange={handleChallanFormData}
                    disabled={screenType == "View"}
                    required
                    />
                </FormControl>
            </div>
            <div className="col-md-4 p-2">
                <FormControl fullWidth>
                    <TextField
                        type="date"
                        id="reappropriate_order_date"
                        label="Reappropriation Order Date"
                        name="reappropriate_order_date"
                        value={formData.reappropriate_order_date || ""}
                        onChange={handleReappropriateOrderDateChange}
                        disabled={screenType == "View" || !min_reappropriate_order_date}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            min: min_reappropriate_order_date || "",
                        }}
                        error={
                            !!formData.reappropriate_order_date &&
                            !!min_reappropriate_order_date &&
                            !isReappropriateOrderDateValid(
                            formData.reappropriate_order_date,
                            min_reappropriate_order_date
                            )
                        }
                        helperText={
                            formData.reappropriate_order_date &&
                            min_reappropriate_order_date &&
                            !isReappropriateOrderDateValid(
                            formData.reappropriate_order_date,
                            min_reappropriate_order_date
                            )
                            ? `Date cannot be before ${min_reappropriate_order_date}.`
                            : ""
                        }
                        required
                    />
                </FormControl>
            </div>
            <div className="col-md-4 p-2">
                <FormControl fullWidth>
                    <TextField
                        id="reappropriate_order_value"
                        label="Budget Reappropriation Order Value"
                        variant="outlined"
                        type="text"
                        name="reappropriate_order_value"
                        required
                        value={totalAmount.current || ""}
                        onChange={handleChallanFormData}
                        InputProps={{ readOnly: true }}
                        disabled={true}
                    />
                </FormControl>
            </div>
            <div className="col-md-4 p-2">
                <FormControl fullWidth>
                    {
                        screenType == "View" ? 
                        <Box sx={{ width: '100%', textAlign: 'center', marginRight: 2 }}>
                            <a href={formData.order_url} download={formData.order_url} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <Box sx={{ width: '100%', height: 50, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f0f0', borderRadius: 0 }}><InsertDriveFileIcon fontSize="large" color="primary" /></Box>
                            </a>
                        </Box>
                        : <TextField
                            id="attachment"
                            type="file"
                            onChange={handleAttachmentChange}
                            name="attachment"
                            inputProps={{
                                accept: ".pdf,.jpeg,.jpg,.png",
                                multiple: true
                            }}
                            required
                        />
                    }
                </FormControl>
            </div>
        </>
    )
}

export default DefaultFields;